import React from 'react';
import { Modal } from '../basic';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientName?: string;
  onGetStarted?: () => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose, clientName, onGetStarted }) => {
  const handleGetStarted = () => {
    if (onGetStarted) {
      onGetStarted();
      return;
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={() => {}} maxWidth="4xl">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full relative">
        {/* Scrollable container with hidden scrollbar */}
        <div className="welcome-modal-scroll max-h-[85vh] overflow-y-auto">
          {/* Hide scrollbar styles */}
          <style>{`
            .welcome-modal-scroll {
              scrollbar-width: none; /* Firefox */
              -ms-overflow-style: none; /* IE and Edge */
            }
            .welcome-modal-scroll::-webkit-scrollbar {
              display: none; /* Chrome, Safari, Opera */
            }
          `}</style>
          {/* Decorative background pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 opacity-30"></div>

          {/* Header */}
          <div className="relative bg-gradient-to-r from-green-600 via-green-700 to-emerald-700 p-8 text-center">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-full">
              <div className="absolute top-4 left-8 w-3 h-3 bg-white/20 rounded-full"></div>
              <div className="absolute top-8 right-12 w-2 h-2 bg-white/30 rounded-full"></div>
              <div className="absolute bottom-6 left-16 w-4 h-4 bg-white/10 rounded-full"></div>
              <div className="absolute bottom-4 right-8 w-5 h-5 bg-white/15 rounded-full"></div>
            </div>

            <div className="relative z-10">
              <div className="flex justify-center mb-3">
                <div className="relative">
                  <div className="absolute inset-0 bg-white rounded-2xl blur-lg opacity-30 scale-110"></div>
                  <img
                    src="/images/logo-notext.png"
                    alt="StatDog Logo"
                    className="relative h-20 w-20 object-contain bg-white rounded-2xl p-3 shadow-lg"
                  />
                </div>
              </div>

              <div>
                <h1 className="text-3xl font-bold text-white drop-shadow-lg mb-3">Welcome to STATDOG!</h1>
                <p className="text-green-200 text-xl mb-1 font-semibold leading-relaxed">
                  Your account is now active! Let's get your team set up for success.
                </p>
                <p className="text-white/70 text-lg">Follow these simple steps to start capturing real-time stats</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="relative p-8 bg-gradient-to-b from-white to-gray-50">
            {/* Pro Tip */}
            <div className="relative overflow-hidden bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200/50 rounded-2xl p-6 mb-6">
              <div className="absolute top-0 right-0 w-16 h-16 bg-amber-200/20 rounded-full -mr-8 -mt-8"></div>
              <div className="relative flex items-start space-x-4">
                <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-xl w-12 h-12 flex items-center justify-center text-2xl shadow-lg">
                  💡
                </div>
                <div>
                  <h4 className="font-bold text-amber-900 mb-2 text-lg">Pro Tip</h4>
                  <p className="text-amber-800 leading-relaxed">
                    You can set up everything in any order, but we recommend starting with players first. Don't worry -
                    you can always come back and add more data later!
                  </p>
                </div>
              </div>
            </div>

            {/* Setup Steps */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Step 1 */}
              <div className="group relative overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border-2 border-blue-200/50 hover:border-blue-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                <div className="absolute top-0 right-0 w-20 h-20 bg-blue-200/30 rounded-full -mr-10 -mt-10"></div>
                <div className="relative p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl w-12 h-12 flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                      1
                    </div>
                    <div>
                      <h3 className="font-bold text-blue-900 mb-2 text-lg">
                        <span className="mr-2">👥</span>Add Your Players
                      </h3>
                      <p className="text-blue-800 leading-relaxed">
                        Start by adding your team roster with player numbers and positions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="group relative overflow-hidden bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border-2 border-green-200/50 hover:border-green-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                <div className="absolute top-0 right-0 w-20 h-20 bg-green-200/30 rounded-full -mr-10 -mt-10"></div>
                <div className="relative p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-2xl w-12 h-12 flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                      2
                    </div>
                    <div>
                      <h3 className="font-bold text-green-900 mb-2 text-lg">
                        <span className="mr-2">🏆</span>Set Up Opponents
                      </h3>
                      <p className="text-green-800 leading-relaxed">
                        Add the teams you'll be playing against this season.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="group relative overflow-hidden bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl border-2 border-purple-200/50 hover:border-purple-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                <div className="absolute top-0 right-0 w-20 h-20 bg-purple-200/30 rounded-full -mr-10 -mt-10"></div>
                <div className="relative p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-2xl w-12 h-12 flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                      3
                    </div>
                    <div>
                      <h3 className="font-bold text-purple-900 mb-2 text-lg">
                        <span className="mr-2">📅</span>Create Your Season
                      </h3>
                      <p className="text-purple-800 leading-relaxed">Define your season dates and settings.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="group relative overflow-hidden bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl border-2 border-orange-200/50 hover:border-orange-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                <div className="absolute top-0 right-0 w-20 h-20 bg-orange-200/30 rounded-full -mr-10 -mt-10"></div>
                <div className="relative p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-2xl w-12 h-12 flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                      4
                    </div>
                    <div>
                      <h3 className="font-bold text-orange-900 mb-2 text-lg">
                        <span className="mr-2">🎯</span>Schedule Games
                      </h3>
                      <p className="text-orange-800 leading-relaxed">
                        Add your game schedule and start capturing stats!
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 5 - Full width */}
              <div className="lg:col-span-2 group relative overflow-hidden bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl border-2 border-indigo-200/50 hover:border-indigo-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-200/30 rounded-full -mr-12 -mt-12"></div>
                <div className="relative p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-2xl w-12 h-12 flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                      5
                    </div>
                    <div>
                      <h3 className="font-bold text-indigo-900 mb-2 text-lg">
                        <span className="mr-2">📊</span>Invite Stat Takers
                      </h3>
                      <p className="text-indigo-800 leading-relaxed">
                        Get parents and volunteers trained to capture game stats in real-time.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="relative bg-gradient-to-r from-gray-50 to-slate-50 p-6 rounded-b-2xl border-t border-gray-200 flex justify-center">
            <button
              onClick={handleGetStarted}
              className="group relative overflow-hidden bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-2xl font-bold text-lg flex items-center justify-center space-x-3 transition-all duration-300 transform order-1 sm:order-2 min-w-[200px]"
            >
              <span className="relative">Get Started</span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default WelcomeModal;
